import { authService } from './../services/authService'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import * as AuthSession from 'expo-auth-session'

// config
import { discovery } from '../config'

// stores
import { useInitializeStore } from './initialize'
import { useHomeStore } from './home'
import { useStoreStore } from './storeInfo'
import { usePersonalStore } from './personalInfo'
import { useScheduleStore } from './schedule'
import { useFormsStore } from './onboarding'
import { useCommunicateStore } from './communicate'

type Store = {
  token?: AuthSession.TokenResponseConfig | null
  requireRefresh: boolean
  clearData: () => void
  setRequiredRefresh: () => void
  getTokenFromCode: (
    code: string,
    request: AuthSession.AuthRequest,
    redirectUri: string,
    discovery: Pick<AuthSession.DiscoveryDocument, 'tokenEndpoint'>
  ) => void
  getNewToken: (
    clientId: string,
    token: AuthSession.TokenResponseConfig,
    discovery: Pick<AuthSession.DiscoveryDocument, 'tokenEndpoint'>
  ) => void
  logout: () => void
  getPersistedAuth: () => void
}

const useAuthStore = create<Store>()(
  devtools((set) => ({
    token: null,
    requireRefresh: false,
    clearData: () => set({ token: null, requireRefresh: false }),
    setRequiredRefresh: async () => {
      const _isTokenFresh = await authService.isPersistedAuthExpired()

      // update state
      set((state) => ({ ...state, requireRefresh: !_isTokenFresh }))
    },
    getTokenFromCode: async (
      code: string,
      request: AuthSession.AuthRequest,
      redirectUri: string,
      discovery: Pick<AuthSession.DiscoveryDocument, 'tokenEndpoint'>
    ) => {
      const _token = await authService.getTokenFromCode(
        code,
        request,
        redirectUri,
        discovery
      )

      // update state
      set((state) => ({ ...state, token: _token, requireRefresh: false }))
    },
    getNewToken: async (
      clientId: string,
      token: AuthSession.TokenResponseConfig,
      discovery: Pick<AuthSession.DiscoveryDocument, 'tokenEndpoint'>
    ) => {
      const _token = await authService.getNewToken(clientId, token, discovery)

      // update state
      set((state) => ({ ...state, token: _token, requireRefresh: false }))
    },
    logout: async () => {
      await authService.revokeToken(useAuthStore.getState().token, discovery)

      // update state
      // set((state) => ({ ...state, token: null, requireRefresh: false }))

      // clear all zustand stores
      useAuthStore.getState().clearData()
      useInitializeStore.getState().clearData()
      useHomeStore.getState().clearData()
      useStoreStore.getState().clearData()
      usePersonalStore.getState().clearData()
      useScheduleStore.getState().clearData()
      useFormsStore.getState().clearData()
      useCommunicateStore.getState().clearData()
    },
    getPersistedAuth: async () => {
      const _token = await authService.getPersistedAuth()

      // update state
      set((state) => ({ ...state, token: _token }))
    }
  }))
)

export { useAuthStore }
